/**
 * ARBETSORDERNS DATALAGER — det de tre orderskärmarna läser och skriver genom.
 *
 * =========================================================================
 * DET FINNS INGEN LISTRUTT FÖR ARBETSORDRAR, OCH DET ÄR MED FLIT.
 * shared/routes.ts: SYNC_PUSH och SYNC_PULL är den ENDA kanalen mot
 * företagsdata — "inga skriv-endpoints per tabell", och ingen läs-endpoint
 * heller. Listan, detaljen och kundväljaren hämtar därför alla ur SAMMA
 * SYNC_PULL, och "Ny arbetsorder" skriver genom SYNC_PUSH.
 * Steg 5 äger den riktiga lagringen (IndexedDB, utkorg, bakgrundssynk). Tills
 * dess hämtar den här modulen HELA firman från since = 0 vid behov och håller
 * resultatet i minnet. Det är medvetet enkelt och medvetet tillfälligt: allt
 * ligger bakom `useArbetsorderlager`, så den dag lagret finns byts innanmätet
 * utan att en enda skärm rör sig.
 * =========================================================================
 *
 * BESLUT 16 SITTER I TYPEN PÅ LAGRET, INTE I SKÄRMEN.
 * `Arbetsorderlager` bär BARA *WorkerRow-typerna — TimeEntryWorkerRow,
 * MileageWorkerRow, CustomerWorkerRow, WorkOrderWorkerRow. En chefs pull ger
 * visserligen de feta raderna, men de läggs i de smala fälten, och en skärm som
 * läser lagret kan därför inte ens SKRIVA `.rate_ore_per_hour`: fälten finns
 * inte i typen. Orderskärmarna behöver inget pris — priset hör hemma i
 * fakturaunderlaget — och det billigaste sättet att aldrig läcka ett timpris är
 * att inte ha det i handen.
 *
 * UNDANTAGEN är `fullOrder` och `fullKund`, som finns för att KUNNA SKRIVA
 * TILLBAKA en order respektive en kund. WorkOrderWrite kräver price_list_id och
 * CustomerWrite kräver både price_list_id och rot_hundredths; en skrivning som
 * satte dem till null för att den smala raden inte bar dem hade TYST RADERAT
 * prisundantaget och kundens ROT-sats — och därmed ändrat priset och ROT-märket
 * på allt som rapporteras därefter (beslut 15). Båda svarar null i montörs- och
 * gästläge, där ingen ändå får skriva på 'put:work_order' eller 'put:customer'
 * (MANAGER_ONLY).
 */

import { useCallback, useEffect, useState } from 'react';
import { SYNC_PULL, SYNC_PUSH } from '@delat/routes';
import { SYNC_PULL_MAX_LIMIT, asMillis, asSeq } from '@delat/types';
import type {
  ClientView,
  CompanyId,
  CustomerId,
  CustomerRow,
  CustomerWorkerRow,
  CustomerWrite,
  ExpenseWorkerRow,
  MaterialLineWorkerRow,
  MemberWorkerRow,
  MileageWorkerRow,
  Millis,
  Minutes,
  OpId,
  Ore,
  Seq,
  SyncOp,
  SyncPullResponse,
  SyncPushResponse,
  TaskRow,
  TimeEntryWorkerRow,
  WorkOrderId,
  WorkOrderPhotoRow,
  WorkOrderRow,
  WorkOrderWorkerRow,
} from '@delat/types';
import { apifel, enhetsId, granska, klienten, lyckades, misslyckades, nyUlid } from '../api';
import type { Klartidfel, Resultat } from '../api';
import { granskaSyncPull, granskaSyncPush } from '../api/granskare/arbetsorder';
import { registreraFirmaminne } from '../app/firmaminne';

/* == Lagret ============================================================== */

export interface Arbetsorderlager {
  readonly vy: ClientView;
  readonly ordrar: readonly WorkOrderWorkerRow[];
  readonly kunder: readonly CustomerWorkerRow[];
  readonly moment: readonly TaskRow[];
  readonly foton: readonly WorkOrderPhotoRow[];
  readonly medlemmar: readonly MemberWorkerRow[];
  readonly tid: readonly TimeEntryWorkerRow[];
  readonly material: readonly MaterialLineWorkerRow[];
  readonly mil: readonly MileageWorkerRow[];
  readonly utlagg: readonly ExpenseWorkerRow[];
  /** Se filhuvudet. null i montörs- och gästläge. */
  readonly fullOrder: (id: WorkOrderId) => WorkOrderRow | null;
  /** Se filhuvudet. null i montörs- och gästläge. */
  readonly fullKund: (id: CustomerId) => CustomerRow | null;
  /**
   * KUNDERNA MED PRISLISTA OCH ROT-SATS, i den fulla vyn. Tom lista i montörs-
   * och gästläge — där finns fälten inte i ändringsmängden alls (beslut 16).
   */
  readonly fullaKunder: readonly CustomerRow[];
  /**
   * true = firman har fler rader än MAX_SIDOR * SYNC_PULL_MAX_LIMIT och allt kom
   * inte med. Skärmen SKA säga det: en arbetsorder som saknas i listan utan att
   * något felade är värre än ett felmeddelande.
   */
  readonly avkortat: boolean;
}

/** Rad med de två synkkolumner sammanslagningen behöver. */
interface Synkrad {
  readonly seq: Seq;
  readonly deleted_at: Millis | null;
}

interface Samlare {
  vy: ClientView;
  readonly ordrar: Map<string, WorkOrderWorkerRow>;
  readonly fulla: Map<string, WorkOrderRow>;
  readonly kunder: Map<string, CustomerWorkerRow>;
  readonly fullaKunder: Map<string, CustomerRow>;
  readonly moment: Map<string, TaskRow>;
  readonly foton: Map<string, WorkOrderPhotoRow>;
  readonly medlemmar: Map<string, MemberWorkerRow>;
  readonly tid: Map<string, TimeEntryWorkerRow>;
  readonly material: Map<string, MaterialLineWorkerRow>;
  readonly mil: Map<string, MileageWorkerRow>;
  readonly utlagg: Map<string, ExpenseWorkerRow>;
}

const tomSamlare = (): Samlare => ({
  vy: 'worker',
  ordrar: new Map(),
  fulla: new Map(),
  kunder: new Map(),
  fullaKunder: new Map(),
  moment: new Map(),
  foton: new Map(),
  medlemmar: new Map(),
  tid: new Map(),
  material: new Map(),
  mil: new Map(),
  utlagg: new Map(),
});

/**
 * Högsta seq vinner. En pull som görs i flera vändor kan få samma rad två
 * gånger om någon skriver mitt i, och då är den med högst seq den senaste
 * (schema/company.sql: varje accepterad skrivning får ett nytt seq).
 */
const slaSamman = <T extends Synkrad>(
  karta: Map<string, T>,
  nyckel: (rad: T) => string,
  rader: readonly T[],
): void => {
  for (const rad of rader) {
    const k = nyckel(rad);
    const gammal = karta.get(k);
    if (gammal === undefined || rad.seq > gammal.seq) karta.set(k, rad);
  }
};

const paId = (rad: { readonly id: string }): string => rad.id;
const paKonto = (rad: { readonly account_id: string }): string => rad.account_id;

/** Mjukraderade rader kommer med i pullen just för att kunna tas bort här. */
const levande = <T extends Synkrad>(karta: Map<string, T>): readonly T[] =>
  [...karta.values()].filter((rad) => rad.deleted_at === null);

const lagg = (s: Samlare, svar: SyncPullResponse): void => {
  s.vy = svar.view;
  if (svar.view === 'full') {
    const c = svar.changes;
    slaSamman(s.ordrar, paId, c.work_order);
    slaSamman(s.fulla, paId, c.work_order);
    slaSamman(s.kunder, paId, c.customer);
    slaSamman(s.fullaKunder, paId, c.customer);
    slaSamman(s.moment, paId, c.task);
    slaSamman(s.foton, paId, c.work_order_photo);
    slaSamman(s.medlemmar, paKonto, c.member);
    slaSamman(s.tid, paId, c.time_entry);
    slaSamman(s.material, paId, c.material_line);
    slaSamman(s.mil, paId, c.mileage);
    slaSamman(s.utlagg, paId, c.expense);
    return;
  }
  // Montör och gäst: SAMMA form, olika urval på servern (beslut 16 och 17).
  const c = svar.changes;
  slaSamman(s.ordrar, paId, c.work_order);
  slaSamman(s.kunder, paId, c.customer);
  slaSamman(s.moment, paId, c.task);
  slaSamman(s.foton, paId, c.work_order_photo);
  slaSamman(s.medlemmar, paKonto, c.member);
  slaSamman(s.tid, paId, c.time_entry);
  slaSamman(s.material, paId, c.material_line);
  slaSamman(s.mil, paId, c.mileage);
  slaSamman(s.utlagg, paId, c.expense);
};

const fardigt = (s: Samlare, avkortat: boolean): Arbetsorderlager => {
  const fulla = s.fulla;
  const fullaKunder = s.fullaKunder;
  return {
    vy: s.vy,
    ordrar: levande(s.ordrar),
    kunder: levande(s.kunder),
    moment: levande(s.moment),
    foton: levande(s.foton),
    medlemmar: levande(s.medlemmar),
    tid: levande(s.tid),
    material: levande(s.material),
    mil: levande(s.mil),
    utlagg: levande(s.utlagg),
    fullOrder: (id) => {
      const rad = fulla.get(id);
      return rad === undefined || rad.deleted_at !== null ? null : rad;
    },
    fullKund: (id) => {
      const rad = fullaKunder.get(id);
      return rad === undefined || rad.deleted_at !== null ? null : rad;
    },
    fullaKunder: levande(fullaKunder),
    avkortat,
  };
};

/* == Hämtningen ========================================================== */

/**
 * Taket för hur många vändor en hämtning tar. 40 * 1000 rader räcker långt för
 * en firma med fem man; blir det fler säger `avkortat` det rakt ut i stället för
 * att listan tyst blir ofullständig.
 */
const MAX_SIDOR = 40;

const hamtaAllt = async (
  firmaId: CompanyId,
  signal: AbortSignal,
): Promise<Resultat<Arbetsorderlager>> => {
  const samlare = tomSamlare();
  let since = asSeq(0);
  let avkortat = false;

  for (let varv = 0; varv < MAX_SIDOR; varv += 1) {
    const svar = await klienten.anrop(
      SYNC_PULL,
      {
        params: { company_id: firmaId },
        data: { since, limit: SYNC_PULL_MAX_LIMIT },
        signal,
      },
      granskaSyncPull,
    );
    if (!svar.ok) return misslyckades(svar.fel);
    lagg(samlare, svar.varde);

    // has_more = true betyder BARA att fler seq återstår, inte att den här
    // vändan gav rader (SyncPullResponse). En tom vända med has_more är NORMAL i
    // montörs- och gästläge och måste följas av en ny pull på next_since.
    if (!svar.varde.has_more) break;
    if (svar.varde.next_since <= since) {
      // MARKÖREN STÅR STILL MEDAN SERVERN SÄGER has_more: vi vet att det finns
      // rader vi inte fick. Ett tyst avbrott hade ritat listan som om den vore
      // hel — en arbetsorder som saknas utan att något felade är värre än ett
      // felmeddelande.
      avkortat = true;
      break;
    }
    since = svar.varde.next_since;
    avkortat = varv === MAX_SIDOR - 1;
  }

  return lyckades(fardigt(samlare, avkortat));
};

/* == Ett litet minne mellan skärmarna ====================================
   Listan, detaljen och "Ny arbetsorder" är tre skärmar men EN datamängd. Utan
   det här skulle varje navigering hämta hela firman på nytt — i ett trapphus
   med en stapel. Minnet är frivilligt: `glomLagret()` efter varje skrivning,
   och en hämtning som är äldre än FARSKHET_MS görs om ändå.
   ======================================================================= */

const FARSKHET_MS = 30_000;

let minne: { firmaId: CompanyId; lager: Arbetsorderlager; hamtat: number } | null = null;

export const glomLagret = (): void => {
  minne = null;
};

/** Töms vid utloggning och firmabyte tillsammans med de andra lagren. */
registreraFirmaminne(glomLagret);

export type Lagerlage =
  | { readonly status: 'laddar' }
  | { readonly status: 'fel'; readonly fel: Klartidfel }
  | { readonly status: 'klart'; readonly lager: Arbetsorderlager };

export interface Lagerstyrning {
  readonly lage: Lagerlage;
  /** Hämtar om, alltid mot nätet. Efter en skrivning eller på "Försök igen". */
  readonly laddaOm: () => void;
}

export const useArbetsorderlager = (firmaId: CompanyId): Lagerstyrning => {
  const [lage, sattLage] = useState<Lagerlage>(() =>
    minne !== null && minne.firmaId === firmaId && Date.now() - minne.hamtat < FARSKHET_MS
      ? { status: 'klart', lager: minne.lager }
      : { status: 'laddar' },
  );
  const [omgang, sattOmgang] = useState(0);

  const laddaOm = useCallback(() => {
    glomLagret();
    sattLage({ status: 'laddar' });
    sattOmgang((n) => n + 1);
  }, []);

  useEffect(() => {
    if (minne !== null && minne.firmaId === firmaId && Date.now() - minne.hamtat < FARSKHET_MS) {
      sattLage({ status: 'klart', lager: minne.lager });
      return;
    }
    const styrning = new AbortController();
    let vaken = true;
    sattLage({ status: 'laddar' });

    void (async () => {
      const svar = await hamtaAllt(firmaId, styrning.signal);
      if (!vaken) return;
      if (!svar.ok) {
        // Ett avbrutet anrop är inte ett fel — skärmen lämnades.
        if (svar.fel.slag === 'nat' && svar.fel.avbrutet) return;
        sattLage({ status: 'fel', fel: svar.fel });
        return;
      }
      minne = { firmaId, lager: svar.varde, hamtat: Date.now() };
      sattLage({ status: 'klart', lager: svar.varde });
    })();

    return () => {
      vaken = false;
      styrning.abort();
    };
  }, [firmaId, omgang]);

  return { lage, laddaOm };
};

/* == Skrivningen ========================================================= */

/**
 * OP_ID ÄR ANROPARENS (beslut 09). Mynta ETT när användaren trycker Spara, spara
 * det med det du ska skicka och använd SAMMA vid varje omsändning — annars blir
 * varje omtag en ny skrivning i stället för ett uppspelat svar.
 */
export const nyttOpId = (): OpId => granska.opId(nyUlid(), 'op_id');
export const nyttOrderId = (): WorkOrderId => granska.workOrderId(nyUlid(), 'work_order.id');
export const nyttKundId = (): CustomerId => granska.customerId(nyUlid(), 'customer.id');
export const nu = (): Millis => asMillis(Date.now());

/* == Kunden ==============================================================
   ROT-SATSEN BOR PÅ KUNDEN OCH INGEN ANNANSTANS. schema/company.sql rad 525-526:
   time_entry.rot_hundredths "fryses fran customer.rot_hundredths och ar 0 om
   kunden saknar ROT". Momentets rot_eligible säger VAD som ger ROT; kundens
   rot_hundredths säger MED HUR MYCKET. Står den på NULL blir varje tidrad
   fryst med 0 — och då är ROT-krysset på momentskärmen utan verkan på
   fakturaunderlaget, oavsett hur många moment som är märkta.

   BÅDA FÄLTEN MÅSTE ALLTID MED. CustomerWrite bär price_list_id och
   rot_hundredths, och den som skriver tillbaka en kund utan dem raderar båda
   tyst. Därför bygger ändringen sin kropp ur den FULLA raden (`fullKund`) och
   aldrig ur den smala.
   ======================================================================== */

/**
 * EN NY KUND. base_version null = "raden ska inte finnas", vilket alltid ger
 * version 1. `rot` är kundens ROT-sats i hundradels procent (30 % = 3000) och
 * null = ingen ROT. CHECK i schemat: satsen får bara sättas på en PRIVATKUND.
 */
export const nyKundOp = (
  id: CustomerId,
  rad: CustomerWrite,
  opId: OpId,
): SyncOp => ({
  op_id: opId,
  client_time: nu(),
  kind: 'put',
  base_version: null,
  table: 'customer',
  id,
  row: rad,
});

/**
 * ÄNDRAD KUND, byggd ur den FULLA raden så att inget fält tappas. Radens
 * NUVARANDE version som base_version — stämmer den inte svarar servern
 * SYNC_CONFLICT, vilket är rätt svar när två telefoner rättar samma kund.
 */
export const andradKundOp = (
  rad: CustomerRow,
  andringar: Partial<CustomerWrite>,
  opId: OpId,
): SyncOp => ({
  op_id: opId,
  client_time: nu(),
  kind: 'put',
  base_version: rad.version,
  table: 'customer',
  id: rad.id,
  row: {
    name: rad.name,
    kind: rad.kind,
    org_number: rad.org_number,
    address: rad.address,
    price_list_id: rad.price_list_id,
    rot_hundredths: rad.rot_hundredths,
    ...andringar,
  },
});

export const skickaOps = async (
  firmaId: CompanyId,
  ops: readonly SyncOp[],
  signal?: AbortSignal,
): Promise<Resultat<SyncPushResponse>> =>
  klienten.anrop(
    SYNC_PUSH,
    {
      params: { company_id: firmaId },
      data: { device_id: enhetsId(), ops },
      ...(signal === undefined ? {} : { signal }),
    },
    granskaSyncPush,
  );

/**
 * FÖRSTA OPERATIONEN SOM FÖLL, som ett fel gränssnittet kan visa.
 *
 * Ett giltigt kuvert svarar ALLTID 200 även om varje operation föll, så
 * `svar.ok` säger ingenting om utfallet — det står i SyncOpResult. Texten hämtas
 * ur shared/errors.ts genom apifel() och ALDRIG ur serverns sträng (api/fel.ts).
 */
export const forstaMisslyckade = (svar: SyncPushResponse): Klartidfel | null => {
  for (const resultat of svar.results) {
    if (resultat.status !== 'failed') continue;
    const kropp = resultat.error;
    // Grenarna finns för att de två SLUTNA detaljformerna ska behålla sin typ
    // hela vägen in i apifel(); se ApifelFor i api/fel.ts.
    if (kropp.code === 'ROW_INVOICED') {
      return apifel('ROW_INVOICED', kropp.detail, kropp.message_sv);
    }
    if (kropp.code === 'PRICE_MISSING') {
      return apifel('PRICE_MISSING', kropp.detail, kropp.message_sv);
    }
    return apifel(kropp.code, kropp.detail, kropp.message_sv);
  }
  return null;
};

/** true = operationen är gjord och ska INTE skickas igen ur samma plan. */
export const arKlar = (svar: SyncPushResponse, opId: OpId): boolean =>
  svar.results.some(
    (r) => r.op_id === opId && (r.status === 'applied' || r.status === 'duplicate'),
  );

/* == Uppslag skärmarna gör om och om igen ================================ */

export const kundMedId = (
  lager: Arbetsorderlager,
  id: CustomerId,
): CustomerWorkerRow | null => lager.kunder.find((k) => k.id === id) ?? null;

export const orderMedId = (
  lager: Arbetsorderlager,
  id: string,
): WorkOrderWorkerRow | null => lager.ordrar.find((o) => o.id === id) ?? null;

export const medlemsnamn = (lager: Arbetsorderlager, kontoId: string | null): string | null => {
  if (kontoId === null) return null;
  return lager.medlemmar.find((m) => m.account_id === kontoId)?.name ?? null;
};

/**
 * ADRESSEN ÄR DET EN HANTVERKARE KÄNNER IGEN ETT JOBB PÅ. Ordern får ha en egen
 * (lgh, port, etapp); saknas den är det kundens, precis som schema/company.sql
 * säger: "address TEXT, -- NULL = kundens adress".
 */
export const adressFor = (
  order: WorkOrderWorkerRow,
  kund: CustomerWorkerRow | null,
): string => order.address ?? kund?.address ?? kund?.name ?? 'Utan adress';

export interface Rapporterat {
  readonly minuter: Minutes;
  readonly materialrader: number;
  readonly meter: number;
  readonly utlaggOren: Ore;
}

/**
 * VAD SOM REDAN ÄR RAPPORTERAT PÅ JOBBET.
 *
 * INGET AV TALEN ÄR ETT PRIS. Minuter är minuter och meter är meter; det enda
 * beloppet är utläggens egna summor, som användaren själv skrev in EX MOMS
 * (ExpenseRow.amount_ore) och som inte kommer ur någon prislista. Arbetets värde
 * finns inte i den här modulen och ska inte räknas fram här — det görs ur de
 * FRYSTA satserna på servern, med rundningsreglerna i company/amounts.ts
 * (beslut 15), och visas i fakturaunderlaget.
 *
 * URVALET FÖLJER VYN, inte den här funktionen: i montörsläge innehåller lagret
 * bara personens EGNA tid-, mil- och utläggsrader (materialrader går ut för
 * alla), så summan är hans egen. Skärmen skriver ut vilket.
 */
export const rapporteratPa = (lager: Arbetsorderlager, orderId: string): Rapporterat => {
  let minuter = 0;
  for (const rad of lager.tid) if (rad.work_order_id === orderId) minuter += rad.minutes;
  let meter = 0;
  for (const rad of lager.mil) if (rad.work_order_id === orderId) meter += rad.meters;
  let utlagg = 0;
  for (const rad of lager.utlagg) if (rad.work_order_id === orderId) utlagg += rad.amount_ore;
  const materialrader = lager.material.filter((rad) => rad.work_order_id === orderId).length;
  return {
    minuter: asMinuter(minuter),
    materialrader,
    meter,
    utlaggOren: asOren(utlagg),
  };
};

/* Summan av märkta heltal är ett heltal av samma sort, men TypeScript vet det
   inte: `a + b` på två Minutes är ett vanligt number. Kontraktets konstruktorer
   sätter märket tillbaka — och prövar samtidigt att summan verkligen blev ett
   heltal, vilket den bara är så länge ingen delat någonstans på vägen. */
const asMinuter = (n: number): Minutes => granska.minuter(n, 'summa.minuter');
const asOren = (n: number): Ore => granska.oren(n, 'summa.oren');
